import React, { useState } from 'react';
import { Row, Col, Card } from 'antd';
import {
  TeamOutlined,
  SendOutlined,
  CheckOutlined,
  InboxOutlined,
  ClockCircleOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { FornecedoresStats } from '../../../../types/dashboard/fornecedores.types';
import CardDetailModal from './CardDetailModal';
import TentativasEnvioModal from './TentativasEnvioModal';

interface InfoCardsProps {
  stats: FornecedoresStats;
  clienteId: string;
}

const InfoCards: React.FC<InfoCardsProps> = ({ stats, clienteId }) => {
  // Estados para controle das modals
  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [tentativasModalVisible, setTentativasModalVisible] = useState<boolean>(false);
  const [selectedCardType, setSelectedCardType] = useState<'totalFornecedores' | 'questionariosEnviados' | 'pendentes' | 'respondidos' | 'naoRespondidos' | null>(null);

  // Cálculos corretos
  const enviosPendentes = stats.totalCadastros - stats.questionariosEnviados;
  
  // Questionários não respondidos: enviados menos respondidos
  const questionariosNaoRespondidos = stats.questionariosEnviados - stats.questionariosRespondidos;
  
  // Estilo comum para os cards
  const cardStyle = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    justifyContent: 'center',
    padding: '16px 12px',
    cursor: 'pointer', // Adicionado cursor pointer para indicar que é clicável
    transition: 'all 0.3s ease', // Transição suave para efeitos hover
  };
  
  // Estilo para hover
  const hoverStyle = {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-3px)',
  };
  
  // Estilo comum para os números
  const numberStyle = {
    fontSize: '28px',
    fontWeight: 'bold' as const,
    marginTop: '8px'
  };
  
  // Estilo para os títulos
  const titleStyle = {
    fontSize: '14px',
    color: '#666',
    marginBottom: '4px'
  };

  // Handler para abrir a modal com o tipo específico de card
  const handleCardClick = (cardType: 'totalFornecedores' | 'questionariosEnviados' | 'pendentes' | 'respondidos' | 'naoRespondidos') => {
    setSelectedCardType(cardType);
    setModalVisible(true);
  };

  // Handler para abrir a modal de tentativas de envio
  const handleTentativasClick = () => {
    setTentativasModalVisible(true);
  };

  // Handler para fechar as modals
  const handleCloseModal = () => {
    setModalVisible(false);
    setSelectedCardType(null);
  };

  const handleCloseTentativasModal = () => {
    setTentativasModalVisible(false);
  };
  
  return (
    <>
      <Row gutter={16} style={{ marginBottom: '20px' }}>
        {/* Card 1: Total de Fornecedores */}
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Card 
            style={cardStyle} 
            bodyStyle={{ padding: 0 }}
            hoverable
            onClick={() => handleCardClick('totalFornecedores')}
          >
            <div style={titleStyle}>Total de Fornecedores</div>
            <div style={{ ...numberStyle, color: '#1890ff' }}>
              <TeamOutlined style={{ marginRight: '8px', fontSize: '28px' }} />
              {stats.totalCadastros}
            </div>
          </Card>
        </Col>
        
        {/* Card 2: Questionários Enviados */}
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Card 
            style={cardStyle} 
            bodyStyle={{ padding: 0 }}
            hoverable
            onClick={() => handleCardClick('questionariosEnviados')}
          >
            <div style={titleStyle}>Questionários Enviados</div>
            <div style={{ ...numberStyle, color: '#52c41a' }}>
              <SendOutlined style={{ marginRight: '8px', fontSize: '28px' }} />
              {stats.questionariosEnviados}
            </div>
          </Card>
        </Col>
        
        {/* Card 3: Pendentes / Não Enviados */}
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Card 
            style={cardStyle} 
            bodyStyle={{ padding: 0 }}
            hoverable
            onClick={() => handleCardClick('pendentes')}
          >
            <div style={titleStyle}>Questionários Não Enviados</div>
            <div style={{ ...numberStyle, color: '#faad14' }}>
              <InboxOutlined style={{ marginRight: '8px', fontSize: '28px' }} />
              {enviosPendentes}
            </div>
          </Card>
        </Col>
        
        {/* Card 4: Questionários Respondidos */}
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Card 
            style={cardStyle} 
            bodyStyle={{ padding: 0 }}
            hoverable
            onClick={() => handleCardClick('respondidos')}
          >
            <div style={titleStyle}>Questionários Respondidos</div>
            <div style={{ ...numberStyle, color: '#722ed1' }}>
              <CheckOutlined style={{ marginRight: '8px', fontSize: '28px' }} />
              {stats.questionariosRespondidos}
            </div>
          </Card>
        </Col>
        
        {/* Card 5: Questionários Não Respondidos */}
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Card 
            style={cardStyle} 
            bodyStyle={{ padding: 0 }}
            hoverable
            onClick={() => handleCardClick('naoRespondidos')}
          >
            <div style={titleStyle}>Questionários Não Respondidos</div>
            <div style={{ ...numberStyle, color: '#ff4d4f' }}>
              <ClockCircleOutlined style={{ marginRight: '8px', fontSize: '28px' }} />
              {questionariosNaoRespondidos}
            </div>
          </Card>
        </Col>

        {/* Card 6: Tentativas de Envios */}
        <Col xs={24} sm={12} md={4} lg={4} xl={4}>
          <Card 
            style={cardStyle} 
            bodyStyle={{ padding: 0 }}
            hoverable
            onClick={handleTentativasClick}
          >
            <div style={titleStyle}>Tentativas de Envio</div>
            <div style={{ ...numberStyle, color: '#13c2c2', fontSize: '22px' }}>
              <ReloadOutlined style={{ marginRight: '8px', fontSize: '28px' }} />
              Ver Detalhes
            </div>
          </Card>
        </Col>
      </Row>

      {/* Modal de detalhes dos cards originais */}
      <CardDetailModal
        visible={modalVisible}
        onClose={handleCloseModal}
        cardType={selectedCardType}
        stats={stats}
        clienteId={clienteId}
      />

      {/* Nova modal de tentativas de envio */}
      <TentativasEnvioModal
        visible={tentativasModalVisible}
        onClose={handleCloseTentativasModal}
        stats={stats}
        clienteId={clienteId}
      />
    </>
  );
};

export default InfoCards;